import React from 'react'
import { theme } from '../theme'
import { fmtDateTime } from '../utils/datetime'
import {
  describeTimestamp,
  type TransmissionMeasurementSource,
} from './transmissionTiming'

/**
 * ONE activatable affordance for "where did this number come from?".
 *
 * WHY THIS FILE EXISTS
 *
 * Three hand-written copies of the same `title=` + `cursor: 'help'` pair already existed
 * across the frontend, and `TransmissionMeasurements.tsx` held a private `Field` that was
 * about to become the fourth. That is not a tidiness complaint: a per-copy affordance is a
 * per-copy accessibility bug, and every copy of it was hover-only — which means it did not
 * exist at all on a tablet, and did not exist at all for anyone driving the page from the
 * keyboard. An operator deciding whether a timestamp is trustworthy is exactly the person
 * who must be able to read its provenance.
 *
 * So this component keeps the existing convention (native `title`, `cursor: 'help'`) and
 * ADDS, per G5.10:
 *
 *   * click / tap to toggle the description open,
 *   * keyboard focus to reveal it, Enter / Space to pin it, Escape to dismiss,
 *   * `aria-describedby` pointing at a description node that is ALWAYS in the DOM — only
 *     visually clipped when closed. A node that is conditionally rendered is a node a
 *     screen reader cannot find, which would make the ARIA reference dangle.
 *
 * TWO LAYOUTS, ONE IMPLEMENTATION. The two consumers disagree about layout, not about
 * behaviour: the measurements panel is a two-column grid (label cell, value cell) and run
 * detail is a flex row with the value pushed right. `layout` picks the wrapper; nothing
 * about the affordance itself branches on it. Giving each page its own component is how the
 * three existing copies happened.
 */

const GREY = theme.textMuted

let sequence = 0

/** A DOM id for the description node. Stable for the lifetime of the component. */
function useDescriptionId(): string {
  const ref = React.useRef<string | null>(null)
  if (ref.current === null) {
    sequence += 1
    ref.current = `hint-${sequence}`
  }
  return ref.current
}

export type HintLayout = 'grid' | 'row'

export interface HintProps {
  label: string
  value: React.ReactNode
  /** The description. When absent this renders as a plain label / value pair. */
  hint?: string
  layout?: HintLayout
  muted?: boolean
  /** Rendered as a small marker after the label — e.g. "provider" or "source not recorded". */
  tag?: string
}

/*
 * THERE IS NO `caution` PROP, AND ITS REMOVAL IS PART OF THIS CHANGE.
 *
 * It styled the marker and the description amber for a reading whose recorded rung was worse than
 * the provider of its leg. The operator declined that framing — *"I dont think we need to display a
 * WARNING, but we should be clear about where the authoritative timestamp came from."* — so a
 * fallback source is now stated by `tag` and explained in the description, and there is no severity
 * for a caller to pass. Keeping an unused prop would leave the next reader a hook to re-introduce
 * the treatment without noticing it had been ruled out.
 */

/**
 * The visually-hidden style for a closed description.
 *
 * Deliberately NOT `display: none` and NOT `visibility: hidden`: both remove the node from
 * the accessibility tree in at least one major screen reader, which is precisely the
 * audience the `aria-describedby` exists for. The clip-rect idiom keeps the text readable
 * to assistive technology while taking no visual space.
 */
const CLIPPED: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
}

export function Hint({
  label, value, hint, layout = 'grid', muted, tag,
}: HintProps) {
  const [open, setOpen] = React.useState(false)
  const [focused, setFocused] = React.useState(false)
  const id = useDescriptionId()
  const shown = !!hint && (open || focused)

  const activate = (event: React.SyntheticEvent) => {
    // A label inside a row that is itself clickable must not hand the click upwards.
    event.stopPropagation()
    setOpen(o => !o)
  }

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault()
      setOpen(o => !o)
    } else if (event.key === 'Escape') {
      setOpen(false)
    }
  }

  const labelNode = hint ? (
    <span
      // Native title first: the hover convention this project already uses everywhere,
      // kept so nothing that worked before stops working.
      title={hint}
      style={{
        color: GREY,
        cursor: 'help',
        borderBottom: `1px dotted ${theme.border}`,
        outlineOffset: '2px',
      }}
      tabIndex={0}
      role="button"
      aria-expanded={open}
      aria-describedby={id}
      onClick={activate}
      onKeyDown={onKeyDown}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    >
      {label}
      {tag ? (
        <span
          style={{
            marginLeft: '0.35rem',
            fontSize: '0.68rem',
            padding: '0 0.3rem',
            borderRadius: 3,
            border: `1px solid ${theme.border}`,
            color: GREY,
            textTransform: 'uppercase',
            letterSpacing: '0.03em',
          }}
        >
          {tag}
        </span>
      ) : null}
      <span aria-hidden="true" style={{ marginLeft: '0.3rem', fontSize: '0.7rem', opacity: 0.7 }}>
        ⓘ
      </span>
    </span>
  ) : (
    <span style={{ color: GREY }}>{label}</span>
  )

  // Always rendered, only clipped — see CLIPPED.
  const description = hint ? (
    <span
      id={id}
      style={
        shown
          ? {
              display: 'block',
              marginTop: '0.25rem',
              fontSize: '0.72rem',
              lineHeight: 1.45,
              color: GREY,
              fontStyle: 'italic',
              maxWidth: '46rem',
            }
          : CLIPPED
      }
    >
      {hint}
    </span>
  ) : null

  if (layout === 'row') {
    return (
      <div style={{ padding: '0.3rem 0', borderBottom: '1px solid #2a2a2a' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem' }}>
          {labelNode}
          <span
            style={{
              fontSize: '0.8rem',
              maxWidth: '60%',
              textAlign: 'right',
              wordBreak: 'break-word',
              color: muted ? GREY : undefined,
            }}
          >
            {value}
          </span>
        </div>
        {description}
      </div>
    )
  }

  return (
    <>
      {labelNode}
      <span style={{ color: muted ? GREY : theme.text, wordBreak: 'break-word' }}>
        {value}
        {description}
      </span>
    </>
  )
}

/**
 * One timestamp, its ONE label, and where that particular value came from.
 *
 * The `column` and the `value` are passed separately and deliberately: the pairing is what
 * a test can read, and a description attached to the wrong value is the exact defect this
 * whole change exists to prevent. `row` is the record the description is derived FROM —
 * provenance is a fact about the row in front of the operator (G5.12), never a per-transport
 * rule applied blind.
 *
 * Both detail pages render every timestamp through this, so the two pages cannot disagree
 * about a label or about a description.
 */
export interface TimestampRowProps {
  column: string
  value?: string | null
  row: TransmissionMeasurementSource
  layout?: HintLayout
}

export function TimestampRow({ column, value, row, layout = 'grid' }: TimestampRowProps) {
  const described = describeTimestamp(column, row)
  return (
    <Hint
      layout={layout}
      label={described.label}
      value={fmtDateTime(value ?? undefined)}
      hint={described.text}
      muted={!value}
      // WHERE IT CAME FROM, ON EVERY TIMESTAMP. The operator's ruling: *"I dont think we need to
      // display a WARNING, but we should be clear about where the authoritative timestamp came
      // from."* Previously the marker appeared only for an INFERRED or INTERNAL value and a
      // recorded-but-degraded rung was rendered as an amber caution instead — so the surface
      // shouted on the readings it distrusted and said nothing about the rest. `sourceTag` is
      // always populated, so every timestamp on the page names its source and none is singled
      // out for alarm. The full sentence, including what a fallback source costs, is still in
      // the description the tag opens.
      tag={described.sourceTag}
    />
  )
}
